import { useDroppable } from "@dnd-kit/core";
import { useMemo } from "react";
import { Card } from "@volca/ui/components/ui/card";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { sortDateDescending } from "@/core/utils/date";
import {
  REFERRAL_COLUMN,
  REFERRAL_COLUMN_TO_HEADER,
} from "@/referrals/constants";
import type {
  IReferral,
  ReferralColumn as ReferralColumnType,
} from "@/referrals/referral";
import ReferralCard from "@/referrals/referral-kanban-view/referral-card/referral-card";
import { ReferralCardSkeleton } from "@/referrals/referral-kanban-view/referral-card/referral-card";
import NumberOfReferrals from "./number-of-referrals";

interface ReferralColumnProps {
  column: ReferralColumnType;
  referralsMap: { [key: string]: IReferral[] };
  isLoading?: boolean;
  activeColumn?: ReferralColumnType;
  activeId?: string | null;
  selectedReferralId?: string;
  onCardClick: (referral: IReferral) => void;
  onNextColumn: (column: ReferralColumnType, referral: IReferral) => void;
  onPayout: (referral: IReferral) => void;
  onLost?: (referral: IReferral) => void;
  onMoveToPaid?: (referral: IReferral) => void;
  onDelete?: (referral: IReferral) => void;
}

const COLUMN_TO_SKELETON_NUMBER = {
  [REFERRAL_COLUMN.inProgress]: 3,
  [REFERRAL_COLUMN.scheduled]: 2,
  [REFERRAL_COLUMN.closed]: 3,
  [REFERRAL_COLUMN.new]: 5,
} as const;

const ReferralColumn = ({
  column,
  referralsMap,
  isLoading = false,
  activeColumn,
  activeId,
  selectedReferralId,
  onCardClick,
  onNextColumn,
  onPayout,
  onLost,
  onMoveToPaid,
  onDelete,
}: ReferralColumnProps) => {
  const { setNodeRef, isOver } = useDroppable({
    id: column,
  });

  const showBorder = isOver && activeColumn !== column;

  const referrals = useMemo(
    () =>
      [...(referralsMap[column] || [])].sort((a, b) =>
        sortDateDescending(a.created_at, b.created_at),
      ),
    [referralsMap, column],
  );

  return (
    <Stack
      align="start"
      gap={4}
      ref={setNodeRef}
      className={cn(
        // A fixed, readable width while the board scrolls sideways. On desktop
        "relative min-h-72 w-72 shrink-0 gap-4 rounded-t-lg rounded-b-2xl border-2 border-transparent xl:w-auto xl:min-w-48 xl:flex-1 xl:basis-0",
        { "border-2 border-brand-line": showBorder },
      )}
    >
      <Card className="sticky top-0 z-10 w-full gap-0 py-0">
        <Stack
          gap={4}
          direction="row"
          justify="between"
          align="center"
          className="min-h-12 w-full px-4"
        >
          <Stack direction="row" align="center" gap={2}>
            <Text variant="label">{REFERRAL_COLUMN_TO_HEADER[column]}</Text>
            <NumberOfReferrals
              count={referrals.length}
              column={column}
              isLoading={isLoading}
            />
          </Stack>
        </Stack>
      </Card>

      <Stack className="w-full" align="start" gap={4}>
        {isLoading
          ? Array.from({ length: COLUMN_TO_SKELETON_NUMBER[column] }).map(
              (_, index) => (
                <ReferralCardSkeleton
                  key={`loading-${index}`}
                  column={column}
                />
              ),
            )
          : referrals.map((referral) => (
              <ReferralCard
                key={referral.id}
                referral={referral}
                column={column}
                isDragging={activeId === referral.id}
                isSelected={selectedReferralId === referral.id}
                onClick={() => onCardClick(referral)}
                onNextColumn={(column) => onNextColumn(column, referral)}
                onPayout={() => onPayout(referral)}
                onLost={() => onLost?.(referral)}
                onMoveToPaid={() => onMoveToPaid?.(referral)}
                onDelete={() => onDelete?.(referral)}
              />
            ))}
      </Stack>
    </Stack>
  );
};

export default ReferralColumn;
